/**
 * OTL pre-order — PRODUCT PAGE. Badge + the add-to-cart split.
 *
 * Depends on otl-preorder-core.js (window.OTLPreorderCore) for all maths.
 *
 * ── WHY IT'S BUILT THIS WAY (Retina 6.3.0 specifics, verified by reading the bundle)
 *
 * 1. TWO theme handlers sit on [data-product-atc], both bubble-phase:
 *      - ProductDetails (retina.js): validates that a variant is chosen.
 *      - CartDrawer     (retina.js): does the real POST /cart/add.js and opens the drawer.
 *    Neither emits an event we can hook, and `new CartDrawer()` is discarded, so
 *    there is no instance to monkey-patch. A CAPTURE-phase listener on `document`
 *    therefore runs before both, and stopImmediatePropagation() kills both at once.
 *
 * 2. We add BOTH lines in ONE POST /cart/add.js using the `items:[...]` form.
 *    Two sequential POSTs would trip theme.liquid's fetch monkey-patch, which
 *    reopens the cart drawer on any /cart/add and throttles to one open per 800ms —
 *    the second add's drawer refresh would be silently swallowed.
 *
 * 3. FAIL OPEN, ALWAYS. Checkout must never be blocked by this script; the Phase 5
 *    checkout function is the hard backstop. So we only ever take over the click
 *    once the split is computed and we know we can act — any error before that and
 *    we simply return, leaving Retina's own handler untouched.
 */
(function () {
  'use strict';

  var core = window.OTLPreorderCore;
  if (!core) return; // core failed to load — leave the theme entirely alone

  var cfg = window.OTLPreorderConfig || {};
  var MINIMUM = cfg.minimum || core.DEFAULT_MINIMUM;
  /** How long a made-to-order run takes, in the customer's words. Config, not code. */
  var LEAD_TIME = cfg.leadTime || '~3 weeks';

  // Set by us for the duration of our own fetch so theme.liquid's /cart/add
  // monkey-patch (drawer auto-open) still fires exactly once, as it would normally.
  /**
   * Monotonic. Wall-clock `Date.now()` moves under NTP corrections: a forward jump
   * would open the staleness window instantly, a backward one would make the elapsed
   * time negative and swallow every click until the clock caught up.
   */
  function now() {
    return (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
  }

  var handling = false;
  /**
   * When the in-flight add started. `handling` alone would be a foot-gun: if a
   * /cart/add.js ever failed to settle, every later click would be swallowed and the
   * customer could not add anything at all for the rest of the page view. After this
   * long we assume the flag is stale and let a fresh click through.
   */
  var handlingSince = 0;
  /** The button that owns the in-flight add — NOT a page-wide lock (see onAtcClick). */
  var handlingBtn = null;
  var ADD_TIMEOUT_MS = 12000;
  /**
   * The re-verify read that can now precede the add. Bounded HERE rather than trusting
   * OTLPreorderCart.refresh()'s own 8s: refresh() returns the IN-FLIGHT chain when one
   * is already running, and that chain re-runs a queued read inside its own tail — so
   * two chained reads (16s+) are reachable and 8s is not an upper bound at all.
   */
  var REVERIFY_BUDGET_MS = 8000;
  /**
   * MUST outlive the whole chain, not one request. A hardcoded 15000 was shorter than
   * the worst case, which opened a window where a click fell through to Retina and
   * reproduced the very bypass this guard exists to close.
   *
   * The chain is now: re-verify, then an add, then possibly a fallback add — and each
   * postAdd is bounded by its RACE timer at ADD_TIMEOUT_MS + 500, not ADD_TIMEOUT_MS.
   * The previous derivation (2 * ADD_TIMEOUT_MS + 3000 = 27000) covered 25000 of
   * add-time with only 2000 to spare, so adding an 8000 re-verify blew straight
   * through it — 33000 against a 27000 guard. Every term is spelled out so the next
   * person adding a step to this chain has to look at this line.
   */
  var HANDLING_STALE_MS = REVERIFY_BUDGET_MS + 2 * (ADD_TIMEOUT_MS + 500) + 3000;

  function log() {
    if (!cfg.debug) return;
    try { console.log.apply(console, ['[otl-preorder]'].concat([].slice.call(arguments))); } catch (e) {}
  }

  // ── one bounded way to make a request ─────────────────────────────────────
  var PRODUCT_TIMEOUT_MS = 8000;

  /**
   * ⚠️ EVERY request in this file goes through here — same rule, same reasons, as
   * otl-preorder-cart.js's fetchBounded, which was written after the identical
   * defect was found at four call sites over four review rounds.
   *
   * TWO things it gets right that a bare fetch does not:
   *  1. The BODY is read inside the timeout and the timer cleared only afterwards.
   *     fetch() resolves on HEADERS; clearing the timer there throws away the only
   *     thing that can kill a stalled body stream, because abort() can no longer
   *     fire. Verified against a server that sends headers and never finishes.
   *  2. It resolves to { ok, status, statusText, text } and NEVER to a Response, so
   *     the body cannot be read outside the guard by a later edit.
   */
  function fetchBounded(url, opts, timeoutMs) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, timeoutMs) : null;
    var o = {};
    for (var k in opts) { if (Object.prototype.hasOwnProperty.call(opts, k)) o[k] = opts[k]; }
    if (ctrl) o.signal = ctrl.signal;
    var done = fetch(url, o).then(function (r) {
      return r.text().then(function (text) {
        if (timer) clearTimeout(timer);
        return { ok: r.ok, status: r.status, statusText: r.statusText, text: text };
      }, function (err) {
        if (timer) clearTimeout(timer);
        throw err;
      });
    }, function (err) {
      if (timer) clearTimeout(timer);
      throw err;
    });
    /**
     * The race runs even when AbortController EXISTS: Safari 11 and Edge 16/17 have
     * the constructor but ignore `signal` on fetch, so abort() is a no-op there and
     * the deadline would silently not exist — the exact hang this bound is for.
     */
    var raceTimer = null;
    return Promise.race([
      done,
      new Promise(function (_, reject) {
        raceTimer = setTimeout(function () {
          var e = new Error('request timed out: ' + url);
          e.otlTimedOut = true;
          reject(e);
        }, timeoutMs + 500);
      }),
    ]).then(function (v) { if (raceTimer) clearTimeout(raceTimer); return v; },
            function (e) { if (raceTimer) clearTimeout(raceTimer); throw e; });
  }

  /**
   * ⚠️ "Could not read it" must never look like "there was nothing there" — on the
   * add path that difference is the difference between one line and two.
   */
  function parseJsonBody(text) {
    if (!text) return { parsed: false, value: null };
    try { return { parsed: true, value: JSON.parse(text) }; } catch (e) {
      return { parsed: false, value: null };
    }
  }

  // ── product data ──────────────────────────────────────────────────────────
  var _product = null;
  function loadProduct() {
    if (_product) return Promise.resolve(_product);
    var url = (cfg.productUrl || window.location.pathname) + '.js';
    /**
     * ⚠️ BOUNDED, because a stall here silently removes the ONLY enforcement there
     * is on this page.
     *
     * Everything on the product page is wired inside this promise's .then — the
     * variant listeners, "Start a 100pc run", the in-stock add. On a stall none of
     * it binds and nothing retries. Worse, onAtcClick early-returns while `_product`
     * is null, so the split never engages and Retina posts the customer's full
     * quantity UNSTAMPED — which groupPreorderLines cannot see, so the cart bar, the
     * checkout gate and express all read that cart as clear. A customer on a flaky
     * connection could order 200 made-to-order pieces with zero enforcement, on a
     * store where this browser gate is the whole enforcement surface.
     *
     * ⚠️ And it must NOT cache a failure: `_product` is only set on a good parse, so
     * a later call genuinely re-fetches.
     */
    return fetchBounded(url, { credentials: 'same-origin' }, PRODUCT_TIMEOUT_MS)
      .then(function (res) {
        if (!res.ok) throw new Error('product read HTTP ' + res.status);
        var b = parseJsonBody(res.text);
        if (!b.parsed || !b.value || !b.value.variants) {
          throw new Error('product read: unreadable body');
        }
        _product = b.value;
        return _product;
      });
  }

  function variantById(product, id) {
    if (!product || !id) return null;
    for (var i = 0; i < product.variants.length; i++) {
      if (String(product.variants[i].id) === String(id)) return product.variants[i];
    }
    return null;
  }

  /** Structured [{name,value}] for a variant, using product.options for the names. */
  function optionsFor(product, variant) {
    if (!product || !variant) return [];
    var names = (product.options || []).map(function (o) { return (o && o.name) ? o.name : o; });
    return core.zipOptions(names, variant.options || []);
  }

  /** On-hand pieces, floored at 0. Infinity = untracked (no count to exceed). */
  function availableOf(variant) {
    if (!variant) return 0;
    var tracked = variant.inventory_management != null && variant.inventory_management !== '';
    if (!tracked) return Infinity;
    var q = parseInt(variant.inventory_quantity, 10);
    if (isNaN(q)) q = 0;
    return q > 0 ? q : 0;
  }

  // ── DOM ───────────────────────────────────────────────────────────────────
  function variantInput() { return document.querySelector('[data-product-form] [name="id"], [name="id"]'); }
  function quantityInput() { return document.querySelector('[data-quantity-input], input[name="quantity"]'); }
  function atcButton() { return document.querySelector('[data-product-atc]'); }
  function inventoryEl() { return document.querySelector('[data-otl-inventory]'); }
  function inventoryTextEl() { return document.querySelector('[data-otl-inventory-count]'); }

  function requestedQty() {
    var q = quantityInput();
    var n = q ? parseInt(q.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  /**
   * Render the inventory/pre-order state into the theme's existing
   * [data-otl-inventory] block. We deliberately OWN this element rather than
   * adding a competing badge — the block's old inline script said "Sold Out" at
   * qty<=0, which is exactly the message a pre-order must replace. (That script
   * is removed from product-form.liquid in this phase; see the snippet.)
   */
  function render(variant, product) {
    var el = inventoryEl();
    var textEl = inventoryTextEl();
    if (!el || !textEl) return;

    el.classList.remove('otl-inventory--sold-out', 'otl-inventory--low', 'otl-inventory--ok', 'otl-inventory--preorder');

    /**
     * Both early returns below must restore the express button AND the theme's own
     * Add to Cart, or a stale "hidden" state from a previous variant survives onto
     * one where it makes no sense.
     *
     * ⚠️ The two-paths class hides the theme's ATC. Leaving it set on a path that
     * never renders our replacement buttons leaves the product page with NO buy
     * control at all — worse than any bug this phase set out to fix. Repro that
     * caught it: straddling variant (two paths shown) → switch to a service /
     * untracked variant → render() returns here and the ATC stays hidden.
     * The file already applies exactly this discipline to setExpressCheckout; the
     * new class has to earn the same treatment.
     */
    if (!variant) { el.style.display = 'none'; setExpressCheckout(false); setTwoPaths(false); return; }
    el.style.display = 'block';

    var available = availableOf(variant);
    var btn = atcButton();

    if (available === Infinity) {
      // Untracked (service items). No count to show, never a pre-order.
      el.style.display = 'none';
      setAtcLabel(btn, null);
      hideOverflow();
      setExpressCheckout(false);
      setTwoPaths(false); // see the note above — never leave the ATC hidden
      return;
    }

    if (available <= 0) {
      if (core.isPreorderable(variant)) {
        // THE FLIP: what used to read "Sold Out" is now a pre-order offer.
        el.classList.add('otl-inventory--preorder');
        textEl.textContent = 'PRE-ORDER — ' + MINIMUM + 'pc minimum per color';
        setAtcLabel(btn, cfg.preorderLabel || 'Pre-Order');
      } else {
        // Tracked + DENY at 0: Shopify will genuinely refuse this sale, so we
        // must NOT offer a pre-order we cannot honour. Style 6031 sits here
        // until the cutover flips it to CONTINUE.
        el.classList.add('otl-inventory--sold-out');
        textEl.textContent = 'Sold Out';
        setAtcLabel(btn, null);
      }
    } else if (available <= 24) {
      el.classList.add('otl-inventory--low');
      // "Only 16 left" → "16 ready to ship" (approved mockup, screens 01/02). The
      // old wording described a shortage; on a store where anything beyond stock is
      // buyable as a run, what the customer needs to know is how many ship TODAY.
      textEl.textContent = available + ' ready to ship';
      setAtcLabel(btn, null);
    } else {
      el.classList.add('otl-inventory--ok');
      textEl.textContent = available + ' ready to ship';
      setAtcLabel(btn, null);
    }

    renderOverflow(variant, product, available);
  }

  /** Swap the ATC label to "Pre-Order" (and back) without losing Retina's own state. */
  function setAtcLabel(btn, label) {
    if (!btn) return;
    var span = btn.querySelector('[data-product-atc-text]');
    if (!span) return;
    if (label) {
      if (!btn.hasAttribute('data-otl-orig-label')) btn.setAttribute('data-otl-orig-label', span.textContent.trim());
      span.textContent = label;
      btn.classList.add('otl-atc--preorder');
    } else if (btn.hasAttribute('data-otl-orig-label')) {
      span.textContent = btn.getAttribute('data-otl-orig-label');
      btn.removeAttribute('data-otl-orig-label');
      btn.classList.remove('otl-atc--preorder');
    }
  }

  // ── overflow prompt: "only 30 in stock — the rest are pre-order" ──────────
  function overflowEl() {
    var el = document.querySelector('[data-otl-preorder-overflow]');
    if (el) return el;
    var host = inventoryEl();
    if (!host || !host.parentNode) return null;
    el = document.createElement('div');
    el.className = 'otl-preorder-overflow';
    el.setAttribute('data-otl-preorder-overflow', '');
    el.style.display = 'none';
    host.parentNode.insertBefore(el, host.nextSibling);
    return el;
  }

  function hideOverflow() {
    var el = document.querySelector('[data-otl-preorder-overflow]');
    if (el) el.style.display = 'none';
    // Hiding the block that HOLDS the two paths must also un-hide the theme's
    // button, or the two go missing together.
    setTwoPaths(false);
  }

  /**
   * Hide the dynamic checkout button ("Buy with Shop" / Apple Pay / G Pay) whenever
   * the current selection would produce pre-order pieces.
   *
   * ⚠️ THIS IS A REAL HOLE, NOT A NICETY — found in the browser on a live product.
   * `{{ form | payment_button }}` renders an EXPRESS checkout that posts straight
   * from the product form to Shopify's checkout. It never touches /cart, so it
   * bypasses ALL of this phase's work at once: the add-to-cart split never runs
   * (no `_preorder` property ⇒ Phase 3 can't tag the order), the cart grouping never
   * sees the line, and the 100pc gate never fires. A customer could one-click-buy a
   * single piece of a variant sitting at -282.
   *
   * We cannot intercept it — it's Shopify-owned markup and its own checkout — so the
   * only honest move at the theme layer is to not offer it for pre-order pieces. The
   * ordinary ATC button is still right there, and it routes through the cart where the
   * rules live. In-stock buying is untouched.
   *
   * Phase 5's checkout validation function should also catch this server-side, but
   * "should" is not "verified" — and a customer who gets a hard checkout error with no
   * badge and no explanation is a bad outcome even when it's technically enforced.
   */
  function setExpressCheckout(hasPreorderPieces) {
    document.body.classList.toggle('otl-preorder-hide-express', Boolean(hasPreorderPieces));
  }

  /**
   * ── THE TWO PATHS (D13 / decision 1) ──────────────────────────────────────
   *
   * One button cannot honestly describe this click. Ask for 28 of a variant with 27
   * on hand and the truth is: 27 ship now, and the 28th cannot exist on its own —
   * it can only be the start of a 100-piece run. The old UI added all 28 and then
   * ambushed the customer at checkout with a 99-piece shortfall they never asked
   * for, leaving them holding one orphan pre-order piece.
   *
   * So the page offers the two real paths instead, and nobody ends up holding a
   * lone piece they did not mean to start a run with.
   *
   * The theme's own Add-to-Cart is hidden while these show (a third, ambiguous
   * button is exactly the confusion being removed) — but only AFTER ours are in the
   * DOM, and it is restored on every other state, so a render failure can never
   * leave a product page with no way to buy.
   */
  function setTwoPaths(on) {
    document.body.classList.toggle('otl-preorder-two-paths', Boolean(on));
  }

  function runSpecFor(product, variant) {
    var opts = optionsFor(product, variant);
    return {
      productId: product.id,
      handle: product.handle,
      productTitle: product.title,
      color: core.colorOf(opts),
      product: product,
    };
  }

  var _lastSpec = null;

  function renderOverflow(variant, product, available) {
    var el = overflowEl();
    if (!el) return;
    var qty = requestedQty();
    /**
     * MUST pass `claimed` — this has to compute the SAME split the add will.
     *
     * While this was cart-blind it caused two failures, one of them a security hole:
     *  (a) the warning contradicted the add. Stock 27 with 27 already in the cart,
     *      customer asks for 28 → the page said "the other 1 piece is pre-order"
     *      while the add correctly stamped all 28.
     *  (b) WORSE: `setExpressCheckout` is driven from this same split. Same cart,
     *      quantity 10 → cart-blind said preorder = 0 → express was SHOWN, even though
     *      every one of those 10 pieces is beyond stock. One click on Buy-with-Shop
     *      posts straight to checkout, past the cart and past the gate — reopening the
     *      exact hole closed for /cart.
     */
    var claimed = claimedStock(variant);
    var remaining = Math.max(0, available - claimed);
    var split = core.splitQuantity(variant, qty, claimed);
    // Any pre-order piece in the selection ⇒ the express path must not be offered.
    setExpressCheckout(split.preorder > 0);

    _lastSpec = (product && variant && core.isPreorderable(variant)) ? runSpecFor(product, variant) : null;

    // The bar for a run ALREADY open in this colour — never a 0/100 bar on a
    // product nobody has started, which would imply a commitment they never made.
    var runHtml = '';
    try {
      var cart = window.OTLPreorderCart;
      if (cart && _lastSpec && typeof cart.groupFor === 'function') {
        var g = cart.groupFor(_lastSpec.productId, _lastSpec.color);
        if (g && g.units > 0) runHtml = cart.runProgressHtml(g);
      }
    } catch (err) { log('run bar failed', err); }

    var straddles = split.preorder > 0 && remaining > 0;
    var allPreorder = split.preorder > 0 && remaining <= 0;

    /**
     * ⚠️ Only offer the buying buttons once the customer has actually CHOSEN a
     * variant. Below this the block sets `otl-preorder-two-paths`, which CSS-hides
     * the theme's own Add to Cart — so offering a button here while the selection is
     * still "not-selected" replaces a control that validates with one that guesses.
     * The MESSAGE is still worth showing (it is drawn from the display fallback, the
     * same one Liquid painted the badge from); the ACTIONS are not.
     */
    var chosen = Boolean(selectedVariant());

    if (straddles && !chosen) {
      // Explain, but leave the theme's button — and its "pick an option" error — in
      // charge until they have picked.
      el.innerHTML = runHtml +
        '<p class="otl-preorder-overflow__lede">Anything beyond stock is made to order: ' +
          MINIMUM + ' pieces per colour, any mix of sizes, ' + LEAD_TIME +
          '. Choose a colour and size to see what ships now.</p>';
      el.style.display = 'block';
      setTwoPaths(false);
    } else if (straddles) {
      // The approved copy, verbatim in shape: what they asked for, what is ready,
      // and what "made to order" actually costs them in time and pieces.
      el.innerHTML = runHtml +
        '<p class="otl-preorder-overflow__lede">You asked for ' + qty + ' — ' + remaining +
          (remaining === 1 ? ' is' : ' are') + ' ready. Anything beyond stock is made to order: ' +
          MINIMUM + ' pieces per colour, any mix of sizes, ' + LEAD_TIME + '.</p>' +
        '<div class="otl-preorder-overflow__paths">' +
          '<button type="button" class="otl-preorder-btn otl-preorder-btn--primary" data-otl-add-instock="' +
            esc(remaining) + '">Add ' + remaining + ' that ship now</button>' +
          '<button type="button" class="otl-preorder-btn otl-preorder-btn--ghost" data-otl-start-run>Start a ' +
            MINIMUM + 'pc run</button>' +
        '</div>';
      el.style.display = 'block';
      setTwoPaths(true);
    } else if (allPreorder) {
      // Nothing ships now, so there is only ONE honest path. The theme's own
      // button (relabelled "Pre-Order") still works for a single size; the run
      // builder is offered because the minimum is per COLOUR across sizes and a
      // customer cannot reach 100 from one size's stepper.
      var why = claimed > 0 && available > 0
        ? 'Your cart already takes all ' + available + ' in stock, so these ' +
          split.preorder + ' are made to order.'
        : 'These are made to order: ' + MINIMUM +
          ' pieces per colour, any mix of sizes, ' + LEAD_TIME + '.';
      el.innerHTML = runHtml +
        '<p class="otl-preorder-overflow__lede">' + esc(why) + '</p>' +
        '<div class="otl-preorder-overflow__paths">' +
          '<button type="button" class="otl-preorder-btn otl-preorder-btn--ghost" data-otl-start-run>Build the ' +
            MINIMUM + 'pc run</button>' +
        '</div>';
      el.style.display = 'block';
      setTwoPaths(false); // the theme's Pre-Order button is still the primary action
    } else {
      el.innerHTML = runHtml;
      el.style.display = runHtml ? 'block' : 'none';
      setTwoPaths(false);
    }
  }

  /**
   * "Add N that ship now" — a plain, UNSTAMPED add of exactly the in-stock pieces.
   *
   * Deliberately does not go through onAtcClick's split: the whole point of this
   * path is that the customer chose NOT to start a run, so there must be no
   * pre-order line and no orphan piece.
   */
  function addInStockOnly(units) {
    /**
     * ⚠️ THE CHOSEN VARIANT ONLY — never the display fallback.
     *
     * onAtcClick states this rule ("Deliberately the CHOSEN variant, never the
     * display fallback") and returns when nothing is picked. This path guessed
     * instead, and the guess BUYS something: verified in a browser against a
     * Retina-shaped form, picking nothing posted Navy/S, picking only a size posted
     * Navy/L (colour guessed), picking only a colour posted Black/S (size guessed).
     *
     * It is reachable in ordinary use because this theme sets
     * select_first_available_variant:false, so the form reads "not-selected" until
     * BOTH options are chosen — and a wholesale buyer who picks a colour and types a
     * quantity before picking a size gets the two-path block, which hides the
     * theme's own Add to Cart. Retina's "you didn't pick a variant" error can then
     * never fire, because its button is the one now hidden. Nothing in the block
     * names the variant, so the customer is charged for a colour or size they never
     * chose and has no way to see it coming.
     */
    var variant = selectedVariant();
    if (!variant) {
      log('ship-now add with no variant chosen — refusing to guess');
      try {
        window.alert('Please choose a colour and a size first.');
      } catch (e) {}
      return Promise.resolve();
    }
    if (units <= 0) return Promise.resolve();
    return postAdd({ id: variant.id, quantity: units })
      .then(function (r) {
        try {
          if (window.OTLPreorderCart && window.OTLPreorderCart.notePendingClaim) {
            window.OTLPreorderCart.notePendingClaim(variant.id, units);
          }
        } catch (e) { log('claim note failed', e); }
        return r;
      });
  }

  // ── sample box (logged-out only) ──────────────────────────────────────────
  /**
   * A sample of a SOLD-OUT style must not be orderable.
   *
   * WHY (operator, 2026-07-16): a sample is "let me feel the fabric before I commit
   * to 50". If it isn't in stock we have nothing to send — the piece would have to be
   * MADE, which is a 3-week pre-order of one unit. That's nonsense, and it's what
   * happens today: the theme's sample box matches on `variant.available`, and a
   * CONTINUE variant at 0 reports available:true. So a sold-out sample silently
   * becomes a one-piece pre-order.
   *
   * But "no" is the wrong answer too — OTL sometimes has odd pieces lying around
   * that they'd happily send. So: don't offer the button, DO offer the email, and
   * prefill it with the exact style/colour/size so neither side has to work out what
   * is being asked.
   *
   * Note this box is `{% unless customer %}` — logged-out only. The logged-in
   * wholesale path is the add-to-cart flow above, which has its own rules.
   */
  var SAMPLE_EMAIL = 'info@otlapparel.shop';

  function sampleEls() {
    return {
      btn: document.getElementById('otlSampleBtn'),
      colorSel: document.getElementById('otlSampleColor'),
      sizeSel: document.getElementById('otlSampleSizeOpt'),
      hidden: document.getElementById('otlSampleSize'),
    };
  }

  /** The non-colour option value of a variant ("XS"), for display + the email. */
  function sizeOf(product, variant) {
    var opts = optionsFor(product, variant);
    for (var i = 0; i < opts.length; i++) {
      if (!/^colou?r$/i.test(String(opts[i].name || ''))) return opts[i].value;
    }
    return null;
  }

  /**
   * Which variant is the sample box pointing at? We resolve it OURSELVES from the
   * dropdowns rather than reading the theme's hidden field, because that field is
   * only populated for `available` variants — the very case we need to catch.
   */
  function sampleSelectedVariant(product) {
    var els = sampleEls();
    if (!product) return null;

    if (els.colorSel && els.sizeSel) {
      if (!els.colorSel.value || !els.sizeSel.value) return null;
      for (var i = 0; i < product.variants.length; i++) {
        var v = product.variants[i];
        var opts = optionsFor(product, v);
        if (core.colorOf(opts) === els.colorSel.value && sizeOf(product, v) === els.sizeSel.value) return v;
      }
      return null;
    }
    // Single-option products: #otlSampleSize is itself the <select>.
    if (els.hidden && els.hidden.tagName === 'SELECT') return variantById(product, els.hidden.value);
    return null;
  }

  function sampleMsgEl() {
    var el = document.querySelector('[data-otl-sample-oos]');
    if (el) return el;
    var els = sampleEls();
    if (!els.btn) return null;
    var host = els.btn.closest('.otl-sample-box') || els.btn.parentNode;
    el = document.createElement('div');
    el.className = 'otl-sample-oos';
    el.setAttribute('data-otl-sample-oos', '');
    el.style.display = 'none';
    host.appendChild(el);
    return el;
  }

  function renderSampleState() {
    var els = sampleEls();
    if (!els.btn || !_product) return;
    var msg = sampleMsgEl();
    if (!msg) return;

    var v = sampleSelectedVariant(_product);
    // Nothing chosen yet, or an impossible colour/size combo — leave the box alone.
    if (!v) { msg.style.display = 'none'; els.btn.disabled = false; return; }

    var available = availableOf(v);   // Infinity = untracked
    if (available === Infinity || available > 0) {
      msg.style.display = 'none';
      els.btn.disabled = false;
      // The theme only fills this for `available` variants; make sure a genuinely
      // in-stock pick is always addable.
      if (els.hidden && els.hidden.tagName === 'INPUT') els.hidden.value = v.id;
      return;
    }

    // Out of stock: no sample exists to send.
    var code = core.styleCodeFromTitle(_product.title) || _product.title;
    var color = core.colorOf(optionsFor(_product, v));
    var size = sizeOf(_product, v);
    var what = [code, color, size].filter(Boolean).join(' ');
    var subject = 'Sample request: ' + what;
    var body = "Hi — I'd like a sample of " + what + ", but the site says it's out of stock."
      + ' Do you have one available?';
    msg.innerHTML = '<p class="otl-sample-oos__text">'
      + "We're out of stock in this size, so we don't have a sample to send right now."
      + ' Email us — we sometimes have pieces on hand.</p>'
      + '<a class="otl-sample-oos__link" href="mailto:' + SAMPLE_EMAIL
      + '?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body) + '">'
      + 'Email us about a ' + esc(what) + ' sample</a>';
    msg.style.display = 'block';
    els.btn.disabled = true;
    // Blank the theme's field too, so its own click handler can't fire an add.
    if (els.hidden && els.hidden.tagName === 'INPUT') els.hidden.value = '';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function initSampleBox() {
    var els = sampleEls();
    if (!els.btn) return; // logged in, or no sample box on this product

    // The theme's own findVariant() also listens on `change` and sets the hidden
    // field. Run after it (not against it) so we have the last word.
    ['change', 'input'].forEach(function (ev) {
      if (els.colorSel) els.colorSel.addEventListener(ev, function () { setTimeout(renderSampleState, 0); });
      if (els.sizeSel) els.sizeSel.addEventListener(ev, function () { setTimeout(renderSampleState, 0); });
      if (els.hidden && els.hidden.tagName === 'SELECT') els.hidden.addEventListener(ev, function () { setTimeout(renderSampleState, 0); });
    });

    // Last line of defence: if anything still manages to click Add Sample on an
    // out-of-stock pick, stop it here rather than letting a 1-piece pre-order in.
    els.btn.addEventListener('click', function (e) {
      var v = sampleSelectedVariant(_product);
      if (v && availableOf(v) !== Infinity && availableOf(v) <= 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        renderSampleState();
      }
    }, true);

    renderSampleState();
  }

  // ── add to cart ───────────────────────────────────────────────────────────
  /** True when the cart script's snapshot is known to be out of date. */
  function cartSnapshotIsStale() {
    try {
      var c = window.OTLPreorderCart;
      return !!(c && typeof c.snapshotIsStale === 'function' && c.snapshotIsStale());
    } catch (e) { return true; } // unknown ⇒ re-verify; the safe direction
  }

  /**
   * How many of this variant are already in the cart against stock. Read from the
   * cart script's cached snapshot, which its own refresh() keeps current (it re-reads
   * after every /cart/add|change|update via the fetch hook). Synchronous by
   * necessity — onAtcClick has to decide whether to intercept before it can await
   * anything.
   *
   * Returns 0 before the first cart read completes, which degrades the split to its
   * old cart-blind behaviour. Be clear-eyed that 0 is NOT a neutral fallback — it is
   * the most optimistic answer available and it under-stamps. It is tolerable only
   * because the window is one page-load round-trip, and because the cart script now
   * KEEPS a stale snapshot rather than clearing it on a failed read (a stale cart is
   * a floor on claimed stock and errs toward stamping; zero errs the other way).
   */
  function claimedStock(variant) {
    try {
      var c = window.OTLPreorderCart;
      if (c && typeof c.claimedStockFor === 'function') return c.claimedStockFor(variant.id);
    } catch (e) { log('claimedStock failed, treating as 0', e); }
    return 0;
  }

  /**
   * Build the /cart/add.js payload. Returns null when nothing needs splitting,
   * which is our signal to NOT intercept at all.
   */
  function buildItems(product, variant, qty) {
    var split = core.splitQuantity(variant, qty, claimedStock(variant));
    if (split.preorder <= 0) return null; // ordinary in-stock add — leave it to Retina

    var opts = optionsFor(product, variant);
    var color = core.colorOf(opts);
    var items = [];

    if (split.inStock > 0) {
      // Plain line — no property, so it can never count toward the 100.
      items.push({ id: variant.id, quantity: split.inStock });
    }

    // ONE definition of the stamp, shared with the run builder's steppers — see
    // core.preorderProperties. Two spellings of the same stamp would split a single
    // run into two groups that each look short of the minimum.
    items.push({ id: variant.id, quantity: split.preorder, properties: core.preorderProperties(product.title, color) });
    return items;
  }

  /**
   * ⚠️⚠️ THE FALLBACK RULE LIVES HERE, and it is not "was it a timeout".
   *
   * /cart/add.js is not idempotent and commits atomically server-side, so the ONLY
   * safe reason to add again is proof that Shopify REJECTED the first one. Shopify
   * rejects with a non-2xx carrying a JSON body (`{description: …}`) — that, and
   * nothing else, is proof. Every other outcome leaves it unknown whether the add
   * landed:
   *   · a timeout or abort — we stopped listening, Shopify did not stop working;
   *   · a network error mid-flight — the request may have arrived;
   *   · a 200 whose body we cannot read — a lapsed wholesale session or a bot-check
   *     answers /cart/add.js with HTML, and the add underneath it COMMITTED;
   *   · a non-2xx whose body we cannot read — Shopify's Ajax throttle returns 429 as
   *     an HTML page.
   *
   * The old rule tagged only timeouts and let everything else fall through to the
   * caller's plain re-add. Proven against a server that commits every add and then
   * answers badly: 200-HTML and a truncated body BOTH produced "27 + 1 stamped,
   * THEN a plain 28 on top" — 56 pieces with 1 counted toward the run, which is
   * verbatim the outcome the caller's own comment says must never happen.
   *
   * So: `otlShopifyRejected` is set ONLY on a parseable non-2xx, and the caller may
   * degrade only on that. Everything else carries `otlCommitUnknown`.
   */
  function postAdd(body) {
    // Bounded: an add that never settles would leave `handling` true and the button
    // spinning forever. Failing is recoverable — the caller degrades on a proven
    // rejection and, failing that, releases the button. Hanging is not.
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    };
    return fetchBounded('/cart/add.js', opts, ADD_TIMEOUT_MS).then(function (res) {
      var b = parseJsonBody(res.text);
      if (!res.ok) {
        var rejected = new Error(
          (b.value && b.value.description) || res.statusText || ('cart/add ' + res.status)
        );
        // Proof of rejection ONLY when Shopify answered in its own error format.
        if (b.parsed) rejected.otlShopifyRejected = true;
        else rejected.otlCommitUnknown = true;
        throw rejected;
      }
      if (!b.parsed) {
        var unreadable = new Error('cart/add: unreadable body — the add may have committed');
        unreadable.otlCommitUnknown = true;
        throw unreadable;
      }
      return b.value;
    }, function (err) {
      // Abort, timeout, network error: we never learned what Shopify did.
      if (err && (err.name === 'AbortError' || err.otlTimedOut)) err.otlTimedOut = true;
      if (err) err.otlCommitUnknown = true;
      throw err;
    });
  }

  /**
   * ⚠️ ONE place, because the last time this logic existed in one place it was
   * ADDED to one caller and not the other.
   *
   * Round 12 correctly stopped re-adding when an add's outcome is unknown, and gave
   * "Add to Cart" a message. The button beside it — "Add N that ship now", the
   * PRIMARY button in the straddling case — went through the same postAdd, got the
   * same flags, and ended at a `log()` that is a no-op unless debug is on. Verified
   * side by side: same page, same variant, Shopify rejects → "Add to Cart" says so,
   * "Add N that ship now" says nothing; connection drops → same silence. And the
   * consequence round 12 named, reached in two clicks: 27 in-stock pieces became 54,
   * and the customer pays for them.
   *
   * So the message lives here and both callers use it. A third add path added later
   * inherits it by construction rather than by someone remembering.
   */
  function reportAddFailure(err) {
    log('add failed', err);
    try {
      if (err && err.otlCommitUnknown) {
        window.alert(
          "We couldn't confirm whether that was added — your connection dropped or "
          + 'your session may have expired. Please check your cart before adding again, '
          + "so you don't order twice."
        );
      } else {
        window.alert('Sorry — we could not add that to your cart. Please try again.');
      }
    } catch (e) { log('could not show the add failure', e); }
  }

  function onAtcClick(e) {
    var btn = e.target && e.target.closest ? e.target.closest('[data-product-atc]') : null;
    if (!btn) return;

    /**
     * A split add is already in flight — SWALLOW this click, don't return.
     *
     * This used to be `if (!btn || handling) return;`, and the bare return was a real
     * bypass. This is a CAPTURE-phase listener on document, so returning does not mean
     * "do nothing" — it means the event carries on to Retina's own bubble-phase
     * handlers, which do a plain POST /cart/add.js of the FULL requested quantity with
     * no `_preorder` stamp. So double-clicking Add to Cart added the split lines AND
     * an unstamped duplicate: 28 pieces the checkout function counts as zero pre-order
     * units, on a variant with 27 in stock.
     *
     * Every other early return below is a deliberate fail-open ("we are not handling
     * this, let the theme add normally"). This one is the opposite: we ARE handling it,
     * so the second click must die here.
     */
    if (handling && btn === handlingBtn && (now() - handlingSince) < HANDLING_STALE_MS) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      log('add already in flight — swallowing duplicate click');
      return;
    }

    // Respect Retina's own "you didn't pick a variant" state.
    if (btn.closest('.product-form--error-option-unselected')) return;

    // We need product data to decide. If it isn't cached yet we cannot make the
    // call synchronously — so we do nothing and let Retina add normally. The
    // data is prefetched on load, so this is a cold-click edge case only.
    if (!_product) { log('no product data yet — deferring to theme'); return; }

    // Deliberately the CHOSEN variant, never the display fallback.
    var variant = selectedVariant();
    if (!variant) return;

    /**
     * Does our cart snapshot need re-checking before we can trust the split?
     *
     * A stale snapshot is a floor on claimed stock ONLY WHILE THE CART GROWS. The
     * moment it SHRINKS — another tab empties it, a removal whose refresh failed —
     * the snapshot becomes a ceiling and over-reports, so in-stock pieces get stamped
     * `_preorder`. That is far worse than it sounds: nothing ever re-splits a line, and
     * the checkout function trusts a stamp unconditionally (`if (stamped) return qty`),
     * so both layers agree on the wrong answer permanently. The customer asks for 5
     * in-stock pieces and is told "you need to add 95 more" with no way out but
     * emptying the line.
     *
     * So whenever the snapshot claims anything at all — or is known stale — spend one
     * round-trip to re-read before deciding. It costs nothing on the common path
     * (claimed === 0 and fresh), and it collapses the whole staleness class: second
     * tab, failed read, /cart/clear.js.
     */
    var claimedNow = claimedStock(variant);
    var mustReverify = claimedNow > 0 || cartSnapshotIsStale();

    var items;
    try {
      items = buildItems(_product, variant, requestedQty());
    } catch (err) {
      log('split failed, deferring to theme', err);
      return; // fail open — Retina's handler still runs
    }
    // Nothing to split AND a snapshot we trust ⇒ an ordinary in-stock add; Retina's.
    // If we must re-verify we take the click even when the stale split says "nothing",
    // because the fresh answer may well be "everything".
    if (!items && !mustReverify) return;

    // From here we own the click.
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    handling = true;
    handlingBtn = btn;
    handlingSince = now();
    btn.classList.add('cart-drawer--loading');

    var clickedQty = requestedQty(); // captured NOW — the customer may nudge it while we wait

    /**
      * Unstamped units a payload puts in — i.e. the stock it consumes. Asks the SAME
      * predicate the cart uses rather than inferring from the absence of a
      * `properties` key: buildItems is ~140 lines away, and adding any property to
      * the in-stock line would silently zero this and quietly reopen the race, with
      * no test going red.
      */
    function inStockUnitsOf(payload) {
      var n = 0;
      for (var i = 0; i < payload.length; i++) {
        if (!core.lineIsPreorder(payload[i].properties)) n += payload[i].quantity;
      }
      return n;
    }

    /**
     * Tell the cart script immediately, because the button re-arms as soon as this
     * POST resolves while `lastCart` is not refreshed until +300ms plus a round-trip.
     * A second click inside that ~400ms window used to read a pre-add snapshot and
     * re-split against stock that was already spoken for.
     *
     * NOTE this only covers adds WE intercept. A wholly in-stock add (preorder <= 0)
     * makes buildItems return null and is handed to Retina, so no claim is recorded —
     * two rapid 20-piece adds against 27 stock can still both split against a stale
     * cart. Pre-existing, and it cannot mint an unstamped pre-order line on its own
     * (nothing was beyond stock at either decision point), but the window is not
     * closed for that path.
     */
    var claimNoted = false;
    /**
     * Stock still sellable at the moment we decided the split. Set inside the commit
     * below, one microtask after buildItems, so no network response can land between
     * the two — it is the same baseline the split itself used.
     */
    var remainingAtDecision = 0;

    /**
     * Single choke point, and it enforces the pendingClaims invariant instead of
     * relying on every caller to happen to satisfy it.
     *
     * A pending claim may be double-counted (a refresh already in flight when the add
     * commits can return a body containing it and still keep the claim). That is
     * harmless ONLY while every claim saturates the remaining stock, because doubling
     * zero is zero. The plain-add branch introduced by the re-verify work is the first
     * caller that can be PARTIAL — it fires when the fresh split found nothing to
     * split, i.e. `clickedQty <= remaining` — which would make the double count live
     * and stamp in-stock pieces. So a partial claim is dropped rather than recorded.
     *
     * Dropping it just re-enters the already-accepted "wholly in-stock double-add"
     * gap; recording it would create the sticky mis-stamp round 3 existed to remove.
     */
    function noteClaim(units) {
      try {
        if (claimNoted) return;
        /**
         * Compared against the baseline captured at DECISION time, not re-read here.
         * Reading it at claim time measures a POST-add cart: if a refresh landed while
         * our POST was in flight, `claimedStock` already includes this add, so
         * `remaining` shrinks and a partial claim sneaks past the test. Concretely —
         * stock 27, 5 claimed, customer adds 15 (nothing to split, correct); a refresh
         * lands mid-flight showing 20; remaining reads 7; `15 < 7` is false so a claim
         * partial against the 22 it was decided from is recorded, and the next add of
         * that variant is stamped 100% pre-order with 7 pieces genuinely in stock. The
         * error is one-directional — catch-up only ever shrinks the baseline, so it
         * never wrongly DROPS a claim, only wrongly keeps one.
         */
        if (units < remainingAtDecision) {
          log('partial claim of ' + units + ' vs ' + remainingAtDecision + ' remaining — dropped (invariant)');
          return;
        }
        claimNoted = true;
        var c = window.OTLPreorderCart;
        if (c && typeof c.notePendingClaim === 'function') c.notePendingClaim(variant.id, units);
      } catch (e) { log('notePendingClaim failed', e); }
    }

    /**
     * Re-read first when the snapshot is suspect, then split against the truth. We
     * have already taken the click, so if the fresh answer is "nothing to split" we
     * must do that plain add ourselves — Retina will never see this event.
     */
    var decided = mustReverify
      ? Promise.race([
          Promise.resolve().then(function () {
            var c = window.OTLPreorderCart;
            return c && typeof c.refresh === 'function' ? c.refresh() : null;
          }),
          // A real ceiling on the wait, so HANDLING_STALE_MS can be derived from it and
          // the button cannot spin indefinitely on a wedged read.
          new Promise(function (_, reject) {
            setTimeout(function () { reject(new Error('cart re-verify timed out')); }, REVERIFY_BUDGET_MS);
          }),
        ])
          .then(function () {
            try {
              return buildItems(_product, variant, clickedQty);
            } catch (err) { log('re-split failed after re-verify', err); return null; }
          })
          .catch(function (err) {
            /**
             * Reached on the re-verify TIMEOUT above. Note that a failed cart READ does
             * NOT land here: OTLPreorderCart.refresh() absorbs its own rejection and
             * resolves, so the success leg runs and re-splits against an unchanged
             * lastCart — which produces `items` byte-identically anyway. Same outcome,
             * different route; spelled out because the previous comment implied a
             * failed read was handled here.
             */
            log('cart re-verify timed out, using the snapshot split', err);
            return items;
          })
      : Promise.resolve(items);

    decided
      .then(function (finalItems) {
        var stockNow = availableOf(variant);
        remainingAtDecision = isFinite(stockNow) ? Math.max(0, stockNow - claimedStock(variant)) : 0;

        if (!finalItems || !finalItems.length) {
          log('re-verified: nothing to split — plain add of', clickedQty);
          return postAdd({ id: variant.id, quantity: clickedQty })
            .then(function (r) { noteClaim(clickedQty); return r; });
        }
        var units = inStockUnitsOf(finalItems);
        log('splitting add', finalItems);
        return postAdd({ items: finalItems })
          .then(function (r) { noteClaim(units); return r; });
      })
      .catch(function (err) {
        /**
         * ⚠️ DEGRADE ONLY ON PROOF OF REJECTION. Aborting cancels OUR interest, not
         * Shopify's work: /cart/add.js is not idempotent and commits atomically
         * server-side, so anything short of "Shopify said no, in its own error
         * format" may well have landed. Re-adding blind produced exactly the bug this
         * guard exists for — 27 + 1 committed, then a plain 28 piled on top: 56
         * pieces, 1 stamped.
         *
         * The rule used to be "not a timeout ⇒ safe", which let an unreadable 200
         * (lapsed session, bot-check) and a 429 HTML page through to the re-add. It
         * is now the other way round: only `otlShopifyRejected` degrades. See postAdd.
         */
        if (!err || !err.otlShopifyRejected) {
          log('add outcome unknown — NOT retrying (it may have landed)', err);
          throw err;
        }
        /**
         * Genuine failure: degrade rather than strand the customer, and add the
         * quantity they actually clicked with.
         *
         * ⚠️ This line is UNSTAMPED, so the checkout rule cannot see it as pre-order.
         * The previous comment here claimed the Phase 5 function's arithmetic backstop
         * covered that — it does not today: the function is not activated and the
         * `otl.available` metafield it reads exists on zero variants. Until that is
         * live, a fallback add escapes the minimum. Stamping the whole line instead
         * would be worse, not better: it would count in-stock pieces as pre-order and
         * let a group reach 100 on ~73 real pre-order pieces.
         */
        log('Shopify REJECTED the split add — falling back to a plain add (UNSTAMPED)', err);
        return postAdd({ id: variant.id, quantity: clickedQty })
          .then(function (r) { noteClaim(clickedQty); return r; });
      })
      .catch(function (err) {
        /**
         * ⚠️⚠️ SAY SOMETHING. Not saying anything moved the double-add out of the
         * code and into the customer's hands, with LESS information than the code
         * had.
         *
         * The rule above correctly refuses to re-add when the outcome is unknown —
         * the add may have committed. But this catch then swallowed it: the spinner
         * cleared, the button re-armed instantly and nothing reached the screen.
         * Verified end to end against a lapsed wholesale session (200 HTML): click,
         * silence, click again — 56 pieces committed, 2 counted toward the run. That
         * is the exact outcome the rule exists to prevent, reached in two clicks.
         *
         * So: on an unknown outcome, tell them to check the cart before trying
         * again. On a plain failure, tell them it failed. `alert` is what every other
         * customer-facing failure on this surface uses (see the sample box), and it
         * cannot be missed the way an inline message can.
         */
        reportAddFailure(err);
      })
      .then(function () {
        /**
         * ⚠️ Release only OUR add. `handling` is global but `handlingBtn` is not, and
         * Retina's quickshop renders this same snippet — so with two live
         * [data-product-atc] buttons the first add's completion used to unlock a
         * second one still in flight, after which a third click was INTERCEPTED
         * rather than swallowed. Null the button too, or the staleness check keeps
         * comparing against a button nothing is doing.
         */
        if (btn === handlingBtn) {
          handling = false;
          handlingBtn = null;
        }
        btn.classList.remove('cart-drawer--loading');
        // Tell them NOW if this colour is short of the minimum, rather than letting
        // it sit in the cart and ambushing them at checkout. Does not block the add
        // — the 100 is per colour across sizes, so a run has to be buildable.
        if (window.OTLPreorderCart) {
          // Let the drawer's own auto-open settle first, or we fight it for the screen.
          setTimeout(function () {
            try {
              var pr = window.OTLPreorderCart.promptIfShort();
              if (pr && pr.catch) pr.catch(function (e) { log('promptIfShort failed', e); });
            } catch (e) { log('promptIfShort threw', e); }
          }, 900);
        }
      });
  }

  // ── wiring ────────────────────────────────────────────────────────────────
  /**
   * The variant the customer has actually chosen, or null. Used for the
   * add-to-cart split, which must NEVER guess: adding a variant the customer
   * didn't pick would be worse than not intercepting at all.
   */
  function selectedVariant() {
    var input = variantInput();
    if (!input || !input.value || input.value === 'not-selected') return null;
    return variantById(_product, input.value);
  }

  /**
   * The variant to DISPLAY. This theme sets select_first_available_variant:false,
   * so on load the form's value is literally "not-selected" while Liquid has
   * already painted a badge for `selected_or_first_available_variant`. If we
   * rendered from the selection alone we'd blank out that server-painted badge
   * on load — a visible flash of the badge disappearing. So for display we mirror
   * Liquid's own fallback exactly: chosen variant → first available → first.
   */
  /**
   * The option values the customer has actually picked, by option index.
   *
   * The theme's `<options-selection>` renders EITHER a <select> per option or a set
   * of radios, depending on a theme setting, and only writes the hidden [name="id"]
   * once EVERY option is chosen. So a customer who has picked a size and not yet a
   * colour is invisible to the form — which is D5.
   *
   * Returns a sparse array: index → value, with holes where nothing is chosen.
   */
  function chosenOptionValues() {
    var out = [];
    var scope = document.querySelector('[data-options-selection]') ||
      document.querySelector('options-selection') ||
      document.querySelector('[data-variant-selection]');
    if (!scope) return out;

    // Radios first: a checked radio is an unambiguous choice.
    var checked = scope.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      var idx = optionIndexOf(checked[i]);
      if (idx >= 0 && checked[i].value) out[idx] = checked[i].value;
    }
    // Then selects, which the 'select' style renders instead.
    var selects = scope.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      var sIdx = optionIndexOf(selects[j]);
      var val = selects[j].value;
      if (sIdx >= 0 && val && val !== 'not-selected' && out[sIdx] == null) out[sIdx] = val;
    }
    return out;
  }

  /**
   * Which product option a control belongs to. The theme names controls
   * `option1|option2|option3` or `options[Color]`; failing both we fall back to the
   * control's position among its siblings, which matches product.options order.
   */
  function optionIndexOf(el) {
    var name = el.getAttribute('name') || '';

    /**
     * ⚠️ READ OFF THE LIVE DOM. Retina 6.3.0 names these controls after the option
     * ITSELF — `name="Color"`, `name="Size"` — not `option1`/`option2` and not
     * `options[Color]`. Handling only the latter two mapped nothing, so
     * chosenOptionValues() came back empty and D5 was still broken exactly as
     * reported: pick 3XL and the badge kept showing 16, which is XS's stock.
     * No unit test could see this; one click in the browser did.
     */
    var direct = matchOptionName(name);
    if (direct >= 0) return direct;

    var m = name.match(/option(\d+)/i);
    if (m) return parseInt(m[1], 10) - 1;

    var byLabel = name.match(/options?\[([^\]]+)\]/i);
    if (byLabel) {
      var idx = matchOptionName(byLabel[1]);
      if (idx >= 0) return idx;
    }

    var group = el.closest ? el.closest('[data-option-index]') : null;
    if (group) {
      var gi = parseInt(group.getAttribute('data-option-index'), 10);
      if (!isNaN(gi)) return gi;
    }
    return -1;
  }

  /** Index of a product option by its name, case- and space-insensitively. */
  function matchOptionName(name) {
    if (!name || !_product || !_product.options) return -1;
    var want = String(name).trim().toLowerCase();
    if (!want) return -1;
    for (var i = 0; i < _product.options.length; i++) {
      var o = _product.options[i];
      var n = (o && o.name ? o.name : o) || '';
      if (String(n).trim().toLowerCase() === want) return i;
    }
    return -1;
  }

  /**
   * The variant to DISPLAY. This theme sets select_first_available_variant:false,
   * so on load the form's value is literally "not-selected" while Liquid has
   * already painted a badge for `selected_or_first_available_variant`. If we
   * rendered from the selection alone we'd blank out that server-painted badge
   * on load — a visible flash of the badge disappearing. So for display we mirror
   * Liquid's own fallback exactly: chosen variant → first available → first.
   *
   * ⚠️ D5 — but that fallback must respect PARTIAL choices. It used to ignore them
   * entirely, so picking 3XL while the colour was still unset left the badge showing
   * the first available variant's stock: the page said 3XL and the line under it said
   * "Only 16 left", which is XS. The customer is then told the wrong number about the
   * wrong size, and the express-checkout decision is made from it too.
   */
  function displayVariant() {
    var chosen = selectedVariant();
    if (chosen) return chosen;
    if (!_product || !_product.variants || !_product.variants.length) return null;

    var picked = chosenOptionValues();
    var pool = _product.variants.filter(function (v) {
      for (var i = 0; i < picked.length; i++) {
        if (picked[i] == null) continue;
        if (String((v.options || [])[i]) !== String(picked[i])) return false;
      }
      return true;
    });
    // A selection that matches nothing (a combination this product doesn't sell)
    // falls back to the whole list rather than showing an empty badge.
    if (!pool.length) pool = _product.variants;

    for (var k = 0; k < pool.length; k++) {
      if (pool[k].available) return pool[k];
    }
    return pool[0];
  }

  function refresh() {
    loadProduct().then(function (p) { render(displayVariant(), p); }).catch(function () {});
  }

  function init() {
    // The sample box is the LOGGED-OUT surface and has no [data-otl-inventory]
    // block, so it must be wired before that early return or it never runs.
    if (document.getElementById('otlSampleBtn')) {
      loadProduct().then(initSampleBox).catch(function (err) { log('sample init failed', err); });
    }

    if (!inventoryEl()) return; // not a product page with the OTL block

    // Capture phase, on document: beats BOTH of Retina's bubble-phase handlers
    // no matter when they were registered.
    document.addEventListener('click', onAtcClick, true);

    /**
     * ⚠️⚠️ WIRE FIRST, PAINT SECOND. Every listener below used to live INSIDE
     * loadProduct().then, so one failed /product.js left a product page with no
     * working way to buy anything.
     *
     * render() paints "Add N that ship now" and "Start a 100pc run" and sets
     * `body.otl-preorder-two-paths`, which CSS-hides the theme's own Add to Cart. So
     * on recovery — window.OTLPreorderProduct.onCartChanged fires after every cart
     * read and routes into refresh() → loadProduct() — the page REPAINTED both
     * buttons, kept the real ATC hidden, and bound nothing: verified, both buttons
     * clicked, zero /cart/add.js calls. render()'s own comment calls that state
     * "worse than any bug this phase set out to fix".
     *
     * Nothing in here needs the product, so nothing in here should wait for it.
     */
    wireProductListeners();

    loadProduct().then(function (p) {
      render(displayVariant(), p);
    }).catch(function (err) {
      /**
       * Fail OPEN, loudly in the log and invisibly on screen: with no product data
       * we cannot split, so the theme's own Add to Cart must remain the one that
       * works. render() never ran, so `otl-preorder-two-paths` was never set and the
       * theme button is still visible. refresh() re-attempts loadProduct on the next
       * variant change or cart read, so this recovers by itself.
       */
      log('product load failed — leaving the theme ATC in charge', err);
    });
  }

  function wireProductListeners() {
    {
      var input = variantInput();
      if (input) {
        input.addEventListener('change', refresh);
        // Retina swaps the variant value programmatically WITHOUT firing change,
        // so mirror the existing OTL block's defence: observe + re-read on click.
        try {
          new MutationObserver(refresh).observe(input, { attributes: true, attributeFilter: ['value'] });
        } catch (err) {}
      }

      document.addEventListener('click', function (ev) {
        if (!ev.target.closest) return;
        if (ev.target.closest('.options-selection__option-value, .options-selection__option-value-label, .swatch-element, [data-variant-option-value]')) {
          setTimeout(refresh, 30);
          setTimeout(refresh, 200);
        }
      });

      /**
       * The two paths (D13). Delegated from document because renderOverflow
       * replaces this block's innerHTML on every quantity keystroke, so anything
       * bound to the buttons themselves would be thrown away between paint and click.
       */
      document.addEventListener('click', function (ev) {
        var add = ev.target.closest && ev.target.closest('[data-otl-add-instock]');
        if (add) {
          ev.preventDefault();
          ev.stopPropagation();
          if (add.disabled) return;
          add.disabled = true;
          var units = parseInt(add.getAttribute('data-otl-add-instock'), 10);
          addInStockOnly(units)
            // Same message as "Add to Cart" — see reportAddFailure. This used to be a
            // silent log(), so a ship-now add that committed but never answered left
            // the customer with no idea, and a second click doubled the order.
            .catch(reportAddFailure)
            .then(function () {
              add.disabled = false;
              refresh();
            });
          return;
        }

        var run = ev.target.closest && ev.target.closest('[data-otl-start-run]');
        if (run) {
          ev.preventDefault();
          ev.stopPropagation();
          if (!_lastSpec) { log('no run spec for this selection'); return; }
          try {
            var c = window.OTLPreorderCart;
            if (c && typeof c.openRunBuilder === 'function') {
              var pr = c.openRunBuilder(_lastSpec);
              if (pr && pr.catch) pr.catch(function (e) { log('run builder failed', e); });
            }
          } catch (e) { log('run builder threw', e); }
        }
      }, true);

      var q = quantityInput();
      if (q) {
        q.addEventListener('input', refresh);
        q.addEventListener('change', refresh);
      }
      // The +/- buttons write the input without firing input/change.
      document.addEventListener('click', function (ev) {
        if (ev.target.closest && ev.target.closest('[data-quantity-minus], [data-quantity-plus]')) setTimeout(refresh, 20);
      });
    }
  }

  /**
   * The cart script calls this after every successful cart read. Without it the
   * product panel keeps a pre-change snapshot: edit the cart in the drawer and the
   * warning text, the split and the express-button state all go stale on the page
   * behind it.
   */
  window.OTLPreorderProduct = window.OTLPreorderProduct || {};
  window.OTLPreorderProduct.onCartChanged = function () {
    try { if (inventoryEl()) refresh(); } catch (e) { log('cart-changed re-render failed', e); }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
